// 🔧 content_profiles.js
// ✅ Purpose: Defines Content Profiles used by the Content Engine
// ✅ Features:
// - Separates audience/market/branding from language
// - Provides future-ready profile architecture without implementing profile functionality yet
// - Supports future Localization Engine, Event Factory, White Label Organizations, AI content generation, and Accessibility
// - Keeps profile names centralized for future resolver logic
// - Phase 1 uses DEFAULT profile only

/**
 * ===========================================================================
 * CONTENT PROFILE PHILOSOPHY
 * ===========================================================================
 *
 * A Locale answers:
 *
 *      "What language is this content written in?"
 *
 * A Profile answers:
 *
 *      "Who is this content written for?"
 *
 * Examples:
 *
 *      Locale
 *          en-US
 *          es-MX
 *
 *      Profile
 *          default
 *          automotive
 *          education
 *          nonprofit
 *
 * Future combinations:
 *
 *      en-US + automotive
 *      en-US + education
 *      es-MX + automotive
 *      es-MX + education
 *
 * This separation has been intentionally designed so LGV never has to
 * redesign its localization architecture.
 *
 * ===========================================================================
 */

export const CONTENT_PROFILES = {

  /**
   * ----------------------------------------------------------
   * Canonical Default
   * ----------------------------------------------------------
   */

  DEFAULT: "default",

  /**
   * ----------------------------------------------------------
   * LGV Markets
   * ----------------------------------------------------------
   */

  AUTOMOTIVE: "automotive",
  EDUCATION: "education",
  NONPROFIT: "nonprofit",
  FESTIVAL: "festival",
  GOVERNMENT: "government",
  ENTERPRISE: "enterprise",

  /**
   * ----------------------------------------------------------
   * Accessibility
   * ----------------------------------------------------------
   */

  ACCESSIBILITY_SIMPLIFIED: "accessibility_simplified",

  /**
   * ----------------------------------------------------------
   * Future Organization Branding
   * ----------------------------------------------------------
   *
   * Organizations may eventually define
   * custom profile names.
   *
   * Phase 1 intentionally does NOT
   * implement organization profiles.
   */

  ORGANIZATION_DEFAULT: "organization_default",
};

/**
 * Human-readable labels.
 */

export const CONTENT_PROFILE_LABELS = {

  [CONTENT_PROFILES.DEFAULT]:
    "Default",

  [CONTENT_PROFILES.AUTOMOTIVE]:
    "Automotive",

  [CONTENT_PROFILES.EDUCATION]:
    "Education",

  [CONTENT_PROFILES.NONPROFIT]:
    "Nonprofit",

  [CONTENT_PROFILES.FESTIVAL]:
    "Festival",

  [CONTENT_PROFILES.GOVERNMENT]:
    "Government",

  [CONTENT_PROFILES.ENTERPRISE]:
    "Enterprise",

  [CONTENT_PROFILES.ACCESSIBILITY_SIMPLIFIED]:
    "Accessibility Simplified",

  [CONTENT_PROFILES.ORGANIZATION_DEFAULT]:
    "Organization Default",

};

/**
 * Default profile used by Phase 1.
 */

export const DEFAULT_CONTENT_PROFILE =
  CONTENT_PROFILES.DEFAULT;

/**
 * ===========================================================================
 * PROFILE HIERARCHY (Future)
 * ===========================================================================
 *
 * Phase 1 does NOT implement inheritance.
 *
 * This structure documents the approved architecture.
 *
 * Examples:
 *
 * default
 *      │
 *      ├── automotive
 *      ├── education
 *      ├── nonprofit
 *      ├── government
 *      └── enterprise
 *
 * Future:
 *
 * organization_default
 *      │
 *      ├── automotive
 *      ├── education
 *      └── nonprofit
 *
 * ===========================================================================
 */

export const PROFILE_PARENT = {

  [CONTENT_PROFILES.DEFAULT]:
    null,

  [CONTENT_PROFILES.AUTOMOTIVE]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.EDUCATION]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.NONPROFIT]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.FESTIVAL]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.GOVERNMENT]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.ENTERPRISE]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.ACCESSIBILITY_SIMPLIFIED]:
    CONTENT_PROFILES.DEFAULT,

  [CONTENT_PROFILES.ORGANIZATION_DEFAULT]:
    CONTENT_PROFILES.DEFAULT,

};

/**
 * ===========================================================================
 * IMPORTANT
 * ===========================================================================
 *
 * The Content Resolver will eventually combine:
 *
 * Scope
 * +
 * Locale
 * +
 * Profile
 *
 * to determine which bundle should be loaded.
 *
 * Example bundle keys:
 *
 * global_en-US_default_event_home
 *
 * global_es-MX_automotive_home
 *
 * org_abc123_en-US_default_dashboard
 *
 * ===========================================================================
 */